/**
 * CompetitionModals - Modal Management Component
 *
 * Single responsibility: Rendering all modals for CompetitionView.
 * Separates modal UI from main component logic.
 */

import React from 'react';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import PicksUpdatedModal from '@/components/modals/PicksUpdatedModal';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';

interface CompetitionModalsProps {
  // Modal states
  isQuickPickOpen: boolean;
  isClearOpen: boolean;
  isSuccessOpen: boolean;
  isLeaderboardOpen: boolean;

  // Modal handlers
  onCloseQuickPick: () => void;
  onCloseClear: () => void;
  onCloseSuccess: () => void;
  onCloseLeaderboard: () => void;

  // Action handlers
  onConfirmQuickPick: () => void;
  onConfirmClear: () => void;
  onReturnToLobby: () => void;

  // Data
  gameId: string;
  leagueId?: string;
  currentUserId?: string;
  hasExistingSelections: boolean;
}

/**
 * Component that renders all modals for CompetitionView
 */
export const CompetitionModals: React.FC<CompetitionModalsProps> = ({
  isQuickPickOpen,
  isClearOpen,
  isSuccessOpen,
  isLeaderboardOpen,
  onCloseQuickPick,
  onCloseClear,
  onCloseSuccess,
  onCloseLeaderboard,
  onConfirmQuickPick,
  onConfirmClear,
  onReturnToLobby,
  gameId,
  leagueId,
  currentUserId,
  hasExistingSelections,
}) => {
  return (
    <>
      {/* Quick Pick Confirmation Modal */}
      <ConfirmationModal
        isOpen={isQuickPickOpen}
        onClose={onCloseQuickPick}
        title="Quick Pick"
        description="This will randomly select answers for all questions. Continue?"
        confirmText="Quick Pick"
        onConfirm={onConfirmQuickPick}
      />

      {/* Clear Selections Confirmation Modal */}
      <ConfirmationModal
        isOpen={isClearOpen}
        onClose={onCloseClear}
        title="Clear Selections"
        description="This will clear all your current selections. Are you sure?"
        confirmText="Clear All"
        onConfirm={onConfirmClear}
      />

      {/* Picks Updated Success Modal */}
      <PicksUpdatedModal
        isOpen={isSuccessOpen}
        onClose={onCloseSuccess}
        onReturnToLobby={onReturnToLobby}
        isUpdate={hasExistingSelections}
        // TODO: Calculate actual potential win based on selections
        potentialWin="813.95"
        betAmount="10"
      />

      {/* Leaderboard Modal */}
      <PickemLeaderboard
        pickemId={gameId}
        leagueId={leagueId}
        isOpen={isLeaderboardOpen}
        onClose={onCloseLeaderboard}
        currentUserId={currentUserId}
      />
    </>
  );
};
